/**
 * Arrival banners: news whose tap opens another screen (the match-arrival banner opens the post), so
 * one must never sit over a coordinated surface: a tap would navigate behind a sheet that stays up
 * (overlay-system-design §4).
 *
 * One banner per key. Raised while a surface holds or waits for the screen, it waits until one task
 * after the last one closes (`awaitCoordinatedSurfacesIdle`). Already showing when a surface takes
 * the screen (a grant, or a takeover), it is taken down and waits again; a request still in line or
 * a celebration not yet claimed leaves it up, as either may never render. A newer banner with the
 * same key replaces the one waiting or showing.
 */
import { dismissBanner, showBanner, type BannerOptions } from '@components/ui/banner';
import {
  awaitCoordinatedSurfacesIdle,
  isAnyCoordinatedSurfaceBusy,
  isAnyCoordinatedSurfaceOnScreen,
  onCoordinatedSurfacesChange,
} from '@lib/overlay/coordinatedSurfaces';

/**
 * The oldest a banner's news may be, from its arrival: one that waited longer for the screen is
 * dropped instead of shown, and one still up when the app comes back to the foreground after longer
 * is taken down. Later it would be stale news worded as fresh (its title and subtitle were read
 * when it arrived).
 */
export const ARRIVAL_BANNER_MAX_WAIT_MS = 3 * 60 * 1000;

/**
 * How long a dropped key refuses a new banner. The matcher checks a post's status when a recompute
 * starts, not when it publishes, so a recompute running as the post was deleted still announces
 * its matches (backend match_engine `compute_and_store`). The backend gives one recompute five
 * minutes (`MATCHER_EVENT_TIMEOUT_S`), and its thread can run on past that: twice that, with room.
 */
export const ARRIVAL_BANNER_DROP_MEMORY_MS = 10 * 60 * 1000;

/** The most dropped keys remembered at once; past it, the oldest is forgotten first. */
export const ARRIVAL_BANNER_DROP_MEMORY_SIZE = 50;

export interface ArrivalBanner {
  /** One banner per key: a newer one with the same key replaces it, waiting or showing. */
  key: string;
  /**
   * What it shows. Its id and close callbacks belong to this module, and its `onClick` is wrapped:
   * it runs on the first tap only, and only while a banner for this key shows (this one, or a
   * same-key one that replaced it, which the tap ends too).
   */
  options: Omit<BannerOptions, 'id' | 'onDismiss' | 'onAutoClose'>;
  /** Read again before a banner that waited is shown; false drops it. */
  stillWanted: () => boolean;
}

export interface ArrivalBanners {
  /** Shows it now if no coordinated surface holds or waits for the screen, else once none does. */
  raise(banner: ArrivalBanner): void;
  /**
   * Its subject is gone (a deleted post): a waiting banner with this key never shows, a showing one
   * is taken down, and for ARRIVAL_BANNER_DROP_MEMORY_MS no new one with this key is raised.
   */
  drop(key: string): void;
  /**
   * Takes down every banner, waiting or showing: its owner is going away (their taps would use a
   * stale navigate), or the session they arrived in has ended. Their subjects are still there, so
   * a later banner for one is raised as usual.
   */
  dropAll(): void;
  /**
   * Takes down every banner, waiting or showing, that arrived more than ARRIVAL_BANNER_MAX_WAIT_MS
   * ago, for the app coming back to the foreground: sonner pauses a toast's timer while the page is
   * hidden, so a banner up when the app went to the background comes back as fresh news otherwise.
   */
  dropStale(): void;
}

/** A key's banner, waiting or showing, and when its news arrived. */
type Slot =
  | { state: 'waiting'; wait: AbortController; arrivedAt: number }
  | { state: 'showing'; toastId: string; end: () => void; arrivedAt: number };

/** A raised banner, with when it arrived: a take-down and a fresh wait keep that time. */
type Arrival = ArrivalBanner & { arrivedAt: number };

export function createArrivalBanners(): ArrivalBanners {
  const slots = new Map<string, Slot>();
  let showings = 0;
  /** Dropped keys and when, oldest first: a raise for one is refused while it is remembered. */
  const dropped = new Map<string, number>();

  const isDropped = (key: string): boolean => {
    const at = dropped.get(key);
    if (at === undefined) return false;
    if (Date.now() - at <= ARRIVAL_BANNER_DROP_MEMORY_MS) return true;
    dropped.delete(key);
    return false;
  };

  const waitForScreen = (banner: Arrival): void => {
    const wait = new AbortController();
    const slot: Slot = { state: 'waiting', wait, arrivedAt: banner.arrivedAt };
    slots.set(banner.key, slot);
    void awaitCoordinatedSurfacesIdle(wait.signal).then(idle => {
      // Aborted: replaced by a newer banner, dropped, or its owner went away.
      if (!idle || wait.signal.aborted) return;
      // Superseded without an abort: the newer one owns the key, and this one never shows.
      if (slots.get(banner.key) !== slot) return;
      slots.delete(banner.key);
      if (Date.now() - banner.arrivedAt > ARRIVAL_BANNER_MAX_WAIT_MS) return;
      if (!banner.stillWanted()) return;
      show(banner);
    });
  };

  // A new showing takes a fresh toast id: sonner loses a toast shown again under an id it is still
  // removing (banner.test.tsx). Replacing the banner on screen keeps its id, so it updates in place.
  const show = (banner: Arrival, toastId = `${banner.key}#${++showings}`): void => {
    let stopWatching = (): void => undefined;
    let tapped = false;
    // Ends this showing (its tap, a swipe, its timer, or a take-down). Safe to call twice.
    const end = (): void => {
      stopWatching();
      if (slots.get(banner.key) === slot) slots.delete(banner.key);
    };
    const slot: Slot = { state: 'showing', toastId, end, arrivedAt: banner.arrivedAt };
    slots.set(banner.key, slot);
    // A surface that takes the screen while it shows would sit under it: take it down, and wait
    // again. Only once it holds the screen: taking it down for a request that then settles stale,
    // or a celebration acked as it was posted, would bring it back as a new toast, announced again.
    stopWatching = onCoordinatedSurfacesChange(() => {
      if (!isAnyCoordinatedSurfaceOnScreen()) return;
      end();
      dismissBanner(toastId);
      waitForScreen(banner);
    });
    showBanner({
      ...banner.options,
      id: toastId,
      // A tap ends the showing at once. sonner reports it through onDismiss only two frames later,
      // and until then a surface opening would queue the tapped banner to come back, and a newer
      // arrival would reuse this id while sonner removes it, and be lost.
      onClick: () => {
        // The card stays tappable through sonner's exit animation, so only the first tap counts,
        // and only while a banner for this key shows: this one, or a same-key one that replaced it.
        // One taken down for a surface, or dropped, must not navigate.
        const current = slots.get(banner.key);
        if (tapped || current?.state !== 'showing') return;
        tapped = true;
        current.end();
        // A replacement under another id goes too: the tap acted on its news. banner.tsx dismisses
        // only the tapped card's own id.
        if (current.toastId !== toastId) dismissBanner(current.toastId);
        banner.options.onClick();
      },
      onDismiss: end,
      onAutoClose: end,
    });
  };

  const takeDown = (key: string): void => {
    const slot = slots.get(key);
    if (!slot) return;
    slots.delete(key);
    if (slot.state === 'waiting') {
      slot.wait.abort();
      return;
    }
    slot.end();
    dismissBanner(slot.toastId);
  };

  return {
    raise(raised) {
      if (isDropped(raised.key)) return;
      const banner: Arrival = { ...raised, arrivedAt: Date.now() };
      const current = slots.get(banner.key);
      if (current?.state === 'showing') {
        // A banner showing means no surface holds the screen (it is taken down the moment one
        // does): replace it in place.
        current.end();
        show(banner, current.toastId);
        return;
      }
      if (current) current.wait.abort();
      if (isAnyCoordinatedSurfaceBusy()) {
        waitForScreen(banner);
      } else {
        show(banner);
      }
    },
    drop(key) {
      // Delete first, so a key dropped again moves to the newest end.
      dropped.delete(key);
      dropped.set(key, Date.now());
      for (const oldest of dropped.keys()) {
        if (dropped.size <= ARRIVAL_BANNER_DROP_MEMORY_SIZE) break;
        dropped.delete(oldest);
      }
      takeDown(key);
    },
    dropAll() {
      for (const key of [...slots.keys()]) takeDown(key);
    },
    dropStale() {
      const now = Date.now();
      for (const [key, slot] of [...slots]) {
        if (now - slot.arrivedAt > ARRIVAL_BANNER_MAX_WAIT_MS) takeDown(key);
      }
    },
  };
}
